import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Layout Components
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { FloatingButtons } from "@/components/layout/FloatingButtons";
import { BookVisitModal } from "@/components/modals/BookVisitModal";
import { BookVisitProvider } from "@/context/BookVisitContext";
import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { SiteDataProvider } from "@/context/SiteDataContext";

// Public Pages
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ProjectsPage from "@/pages/ProjectsPage";
import CategoryPage from "@/pages/CategoryPage";
import PropertyDetailPage from "@/pages/PropertyDetailPage";
import LandConverterPage from "@/pages/LandConverterPage";
import LoanCalculatorPage from "@/pages/LoanCalculatorPage";
import ContactPage from "@/pages/ContactPage";
import CareersPage from "@/pages/CareersPage";
import BlogListPage from "@/pages/BlogListPage";
import BlogDetailPage from "@/pages/BlogDetailPage";

// Admin Pages
import LoginPage from "@/pages/admin/LoginPage";
import DashboardLayout from "@/pages/admin/DashboardLayout";
import DashboardOverview from "@/pages/admin/DashboardOverview";
import PropertiesList from "@/pages/admin/PropertiesList";
import PropertyForm from "@/pages/admin/PropertyForm";
import JobsList from "@/pages/admin/JobsList";
import JobForm from "@/pages/admin/JobForm";
import LeadsList from "@/pages/admin/LeadsList";
import CategoriesList from "@/pages/admin/CategoriesList";
import CategoryForm from "@/pages/admin/CategoryForm";
import HeroEditor from "@/pages/admin/HeroEditor";
import ArticlesList from "@/pages/admin/ArticlesList";
import ArticleForm from "@/pages/admin/ArticleForm";
// ERP Phase 1
import ERPDashboard from "@/pages/admin/erp/ERPDashboard";
import BuildingsPage from "@/pages/admin/erp/BuildingsPage";
import UnitsPage from "@/pages/admin/erp/UnitsPage";
import TenantsPage from "@/pages/admin/erp/TenantsPage";
import AgreementsPage from "@/pages/admin/erp/AgreementsPage";
import EmiCustomersPage from "@/pages/admin/erp/EmiCustomersPage";
import EmiPlansPage from "@/pages/admin/erp/EmiPlansPage";
import AuditLogsPage from "@/pages/admin/erp/AuditLogsPage";
import ComingSoon from "@/pages/admin/erp/ComingSoon";

const PublicLayout = ({ children }) => (
  <BookVisitProvider>
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <FloatingButtons />
      <BookVisitModal />
    </div>
  </BookVisitProvider>
);

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <SiteDataProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors />
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
              <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
              <Route path="/projects" element={<PublicLayout><ProjectsPage /></PublicLayout>} />
              {/* Dynamic category route */}
              <Route path="/category/:slug" element={<PublicLayout><CategoryPage /></PublicLayout>} />
              {/* Backward-compatible aliases */}
              <Route path="/plots" element={<PublicLayout><CategoryPage slugOverride="plot" /></PublicLayout>} />
              <Route path="/houses" element={<PublicLayout><CategoryPage slugOverride="house" /></PublicLayout>} />
              <Route path="/commercial" element={<PublicLayout><CategoryPage slugOverride="commercial" /></PublicLayout>} />
              <Route path="/property/:id" element={<PublicLayout><PropertyDetailPage /></PublicLayout>} />
              <Route path="/land-size-converter" element={<PublicLayout><LandConverterPage /></PublicLayout>} />
              <Route path="/loan-calculator" element={<PublicLayout><LoanCalculatorPage /></PublicLayout>} />
              <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
              <Route path="/careers" element={<PublicLayout><CareersPage /></PublicLayout>} />
              <Route path="/blog" element={<PublicLayout><BlogListPage /></PublicLayout>} />
              <Route path="/blog/:slug" element={<PublicLayout><BlogDetailPage /></PublicLayout>} />

              {/* Admin Routes */}
              <Route path="/admin/login" element={<LoginPage />} />
              <Route path="/dashboard" element={<DashboardLayout />}>
                <Route index element={<ERPDashboard />} />

                {/* ERP Phase 1 — Rental */}
                <Route path="erp/buildings" element={<BuildingsPage />} />
                <Route path="erp/units" element={<UnitsPage />} />
                <Route path="erp/tenants" element={<TenantsPage />} />
                <Route path="erp/agreements" element={<AgreementsPage />} />
                {/* ERP Phase 1 — EMI */}
                <Route path="erp/emi/customers" element={<EmiCustomersPage />} />
                <Route path="erp/emi/plans" element={<EmiPlansPage />} />
                {/* ERP Phase 1 — System */}
                <Route path="erp/audit-logs" element={<AuditLogsPage />} />

                {/* ERP Phase 2+ placeholders */}
                <Route path="erp/rent-collection" element={<ComingSoon title="Rent Collection" description="Auto-calculated rent + electricity + late charges. One-click receive payment." phase="Phase 2" />} />
                <Route path="erp/rent-bills" element={<ComingSoon title="Rent Bills" description="Immutable auto-generated bill PDFs." phase="Phase 2" />} />
                <Route path="erp/rent-receipts" element={<ComingSoon title="Rent Receipts" description="Auto-generated receipts with PAID stamp + QR." phase="Phase 2" />} />
                <Route path="erp/emi/collection" element={<ComingSoon title="EMI Collection" description="Collect current / arrears / advance / partial / custom EMI." phase="Phase 2" />} />
                <Route path="erp/emi/bills" element={<ComingSoon title="EMI Bills" description="Immutable auto-generated EMI bill PDFs." phase="Phase 2" />} />
                <Route path="erp/emi/receipts" element={<ComingSoon title="EMI Receipts" description="EMI receipts with PAID stamp + QR." phase="Phase 2" />} />
                <Route path="erp/electricity" element={<ComingSoon title="Electricity Billing" description="Previous reading → current reading → auto units + charges." phase="Phase 2" />} />
                <Route path="erp/payments" element={<ComingSoon title="Payments Ledger" description="Unified rent + EMI + electricity payment log." phase="Phase 2" />} />
                <Route path="erp/calendar" element={<ComingSoon title="Calendar" description="Google Calendar-style scheduling with auto-events for rent, EMI, electricity, agreements." phase="Phase 3" />} />
                <Route path="erp/whatsapp" element={<ComingSoon title="WhatsApp Sender" description="Reminder templates + scheduled auto-send at 5/8am/12pm/6pm." phase="Phase 3" />} />
                <Route path="erp/sms" element={<ComingSoon title="SMS Sender" description="SMS reminder templates + scheduled auto-send." phase="Phase 3" />} />
                <Route path="erp/documents" element={<ComingSoon title="Documents" description="Central document vault (agreements, KYC, forms)." phase="Phase 3" />} />
                <Route path="erp/reports" element={<ComingSoon title="Reports" description="Daily / Weekly / Monthly / Yearly rent, EMI, electricity reports. Export to PDF / Excel / CSV." phase="Phase 4" />} />
                <Route path="erp/finance" element={<ComingSoon title="Finance" description="Cash flow, outstanding, month-over-month analytics." phase="Phase 4" />} />
                <Route path="erp/users" element={<ComingSoon title="Users & Staff" description="Multi-user login with Admin / Manager / Accountant / Operator roles." phase="Phase 4" />} />
                <Route path="erp/settings" element={<ComingSoon title="ERP Settings" description="Global late-charge rate, bill formats, notification defaults." phase="Phase 4" />} />

                {/* Website CMS (existing) */}
                <Route path="overview" element={<DashboardOverview />} />
                <Route path="properties" element={<PropertiesList />} />
                <Route path="properties/new" element={<PropertyForm />} />
                <Route path="properties/:id/edit" element={<PropertyForm />} />
                <Route path="categories" element={<CategoriesList />} />
                <Route path="categories/new" element={<CategoryForm />} />
                <Route path="categories/:id/edit" element={<CategoryForm />} />
                <Route path="hero" element={<HeroEditor />} />
                <Route path="articles" element={<ArticlesList />} />
                <Route path="articles/new" element={<ArticleForm />} />
                <Route path="articles/:id/edit" element={<ArticleForm />} />
                <Route path="jobs" element={<JobsList />} />
                <Route path="jobs/new" element={<JobForm />} />
                <Route path="jobs/:id/edit" element={<JobForm />} />
                <Route path="leads" element={<LeadsList />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </SiteDataProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
